import React from 'react'
// libs
import { useDispatch } from 'react-redux'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal } from 'components/UI'
// view components
import AddDashboardForm, { DashboardSettingsFormFields } from './components/Form'
// types
import { IDashboard } from 'types/graphql'
// themes
import { CloseIcon } from 'components/UI/Icons'
// constants
import { Modals } from 'constants/modals'
import { AVAILABLE_TO } from 'constants/security'
// hooks
import { useDashboardsAPI } from 'hooks/useDashboardsAPI'
import { useApolloClient, useCurrentUser, useInitialValues, useModalData } from 'hooks/index'
// graphql
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// views
import { Container } from './views'
// store
import { addDashboard, setDashboardsData, updateDashboard as updateDashboardAction } from 'store/reducers/dashboards'
// utils
import { get } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { getOptionByValue } from 'utils/form'
import { getMemberOrPositionLabel } from 'utils/systemProcess'

const PAPER_PROPS = { style: { overflow: 'hidden' } }

const DashboardSettingsDrawer: React.FC = () => {
  const dispatch = useDispatch()

  const user = useCurrentUser()

  const { client } = useApolloClient()
  const { createDashboard, updateDashboard } = useDashboardsAPI()

  const {
    hideModal,
    modalData: { isOpen, dashboard, shouldCreate }
  } = useModalData<{
    dashboard: IDashboard
    shouldCreate: boolean
  }>({ modalType: Modals.DASHBOARD_SETTINGS_MODAL })

  const closeDrawer = () => {
    hideModal()
  }

  const onFormSubmit = async (data: DashboardSettingsFormFields) => {
    const input = {
      title: data.title,
      active: data.active,
      description: data.description,
      availableTo: {
        // Required by the schema: nobody picked is an empty list, not a missing one
        availableToUserIds: data.availableTo?.map((item) => item.value) || []
      }
    }

    if (shouldCreate) {
      const { id } = await createDashboard(input)

      const dashboard = { id, ...input }

      dispatch(setDashboardsData({ current: dashboard }))
      dispatch(addDashboard(dashboard))
    } else {
      await updateDashboard({ ...input, id: dashboard.id })

      dispatch(updateDashboardAction({ id: dashboard.id, data: input }))
    }
    closeDrawer()
  }

  const getUserAndPositions = async (ids: string[]) => {
    try {
      const usersAndPositions = get(
        await client.query({
          query: USERS_OR_POSITIONS_QUERY,
          variables: { filter: { companyId: user.companyId, ids } }
        }),
        'data.usersOrPositions.data',
        []
      )

      return usersAndPositions.map((item) => ({
        ...item,
        label: getMemberOrPositionLabel(item),
        value: item.id
      }))
    } catch (e) {
      handleError(e, 'DashboardSettingsDrawer.getUserAndPositions')
    }
  }

  const getInitialValues = async () => {
    if (shouldCreate || !dashboard) {
      return {
        active: true
      }
    }

    const availableToIds = dashboard.availableTo.availableToUserIds || []
    const availableToUsersIds = availableToIds.filter(
      (id: string) => id !== AVAILABLE_TO.ALL && id !== AVAILABLE_TO.MANAGERS
    )

    const userAndPositions = await getUserAndPositions(availableToUsersIds)

    const options = [
      {
        label: '(All Users)',
        value: AVAILABLE_TO.ALL
      },
      { label: '(Managers)', value: AVAILABLE_TO.MANAGERS },
      ...(userAndPositions || [])
    ]

    return {
      title: dashboard.title,
      active: dashboard.active,
      description: dashboard.description,
      availableTo: availableToIds.map((id) => getOptionByValue(id, options)),
      dashboardId: dashboard.id
    }
  }

  const initialValues = useInitialValues(getInitialValues, dashboard)

  return (
    <DrawerModal width='500px' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Dashboard Properties</span>
        <CloseIcon onClick={closeDrawer} />
      </Header>
      <Container>
        <AddDashboardForm
          onSubmit={onFormSubmit}
          shouldCreate={shouldCreate}
          onCancelClick={closeDrawer}
          initialValues={initialValues}
        />
      </Container>
    </DrawerModal>
  )
}

export default DashboardSettingsDrawer
